<script lang="ts" setup>
import { FormKitLazyProvider } from '@formkit/vue'
import { FormKitNode } from '@formkit/core'

type Option = { value: string | number; label: string; help?: string }
type CheckboxValue = boolean | (string | number)[]
type CheckboxConditionalProps = {
  value: CheckboxValue
  options?: Option[]
}

const props = defineProps<{
  name?: string
  required: boolean
  label?: string
  htmlLabel?: boolean
  // Support both boolean (single) and array (multiple)
  modelValue?: boolean | (string | number)[]
  // Back-compat: single checkbox value
  value?: boolean
  validation?: string
  validationMessages?: {
    [key: string]: string
  }

  options?: Option[]
  toggle?: boolean
  id?: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', v: boolean | (string | number)[]): void
  // Back-compat if someone uses v-model:value
  (e: 'update:value', v: boolean): void
  // Back-compat DOM-like events expected by usages
  (e: 'input', v: boolean | (string | number)[]): void
  (e: 'change', v: boolean | (string | number)[]): void
  // Expose node when needed
  (e: 'node', v: FormKitNode): void
}>()

const validationString = computed(() => {
  const validationString = props.validation ?? ''
  const v = validationString.split('|')
  if (props.required) {
    v.push(!props.options ? 'accepted' : 'required')
  }

  return v.filter(Boolean).join('|')
})

const isMultiple = computed(() => Boolean(props.options?.length))

const singleChecked = computed<boolean>(() => {
  // v-model wins: Vue casts the absent boolean `value` prop to false, so it must not shadow modelValue
  if (typeof props.modelValue === 'boolean') return props.modelValue
  if (typeof props.value === 'boolean') return props.value
  return false
})

const conditionalProps = computed<CheckboxConditionalProps>(() => {
  // FormKit doesn't understand these conditions
  if (isMultiple.value) {
    // multiple: array
    return {
      value: (props.modelValue as (string | number)[]) ?? [],
      options: props.options,
    }
  }
  // single: boolean
  return {
    value: singleChecked.value,
  }
})

const wrapperClasses = ['custom-checkbox', props.toggle ? 'toggle' : '']

const node = ref<FormKitNode | undefined>()

const setNode = (n: FormKitNode) => {
  node.value = n
  emit('node', n)
}

// Keep FormKit node in sync when parent updates model (only for multiple)
watch(
  () => [props.modelValue, props.options?.length],
  () => {
    if (!node.value) return
    if (!isMultiple.value) return
    const val = Array.isArray(props.modelValue) ? props.modelValue : []
    node.value.input(JSON.parse(JSON.stringify(val)))
  },
)

function handleInput(v: unknown) {
  const nextVal = isMultiple.value ? (Array.isArray(v) ? v : []) : Boolean(v)
  emit('update:modelValue', nextVal)
  if (!isMultiple.value) emit('update:value', nextVal as boolean)
  // Single event API: emit only 'change' for consistency
  emit('change', nextVal)
}
</script>

<template>
<FormKitLazyProvider config-file="true">

  <FormKit
    v-bind="conditionalProps"
    :id="id"
    type="checkbox"
    :aria-required="required"
    :name="name"
    :label="label ?? ''"
    :validation="validationString ?? ''"
    :validation-messages="validationMessages ?? {}"
    :classes="{
      outer: `outer-checkbox ${$attrs.class ?? ''}`,
      wrapper: wrapperClasses.filter(Boolean).join(' '),
      fieldset: 'checkbox-fieldset',
      options: 'cul',
    }"
    @input="handleInput"
    @node="setNode"
  >
    <template v-if="htmlLabel" #label>
      <span class="formkit-label html-label" v-html="label" />
    </template>
  </FormKit>

</FormKitLazyProvider>
</template>

<style lang="scss" scoped>
.outer-checkbox {
  &[data-invalid='true'] {
    :deep(.formkit-inner) {
      &:before {
        --form-input-border-color: var(--c-red-90) !important;
        --form-input-label-color: var(--c-red-90) !important;
        --form-input-background: var(--c-red-5) !important;
      }
    }
    :deep(.formkit-label) {
      color: var(--c-red-90) !important;
    }
  }
}

:deep(.custom-checkbox) {
  display: flex;
  gap: 16px;

  &[data-checked='true'] {
    --form-input-background: var(--c-blue-100);
    .formkit-inner {
      &:after {
        opacity: 1;
        visibility: visible;
      }
    }
  }

  &.toggle {
    --form-input-border-color: var(--c-blue-100);
    --toggle-circle-size: 17px;
    --toggle-circle-gap: 4px;

    &[data-checked='true'] {
      .formkit-inner {
        &:before {
          background: var(--form-input-border-color);
          border-color: var(--form-input-border-color);
        }

        &:after {
          background: var(--c-white);
          left: calc(100% - var(--toggle-circle-size) - var(--toggle-circle-gap));
        }
      }
    }
    .formkit-inner {
      --input-width: 45px;
      --input-height: 25px;

      &:before {
        border-radius: 100px;
      }

      &:after {
        background: var(--form-input-border-color);
        border-radius: 50%;

        width: var(--toggle-circle-size);
        height: var(--toggle-circle-size);

        left: var(--toggle-circle-gap);

        transform: translate(0, -50%);

        opacity: 1;
        visibility: visible;

        @include transitions(left);
      }
    }
  }

  .formkit-inner {
    --input-height: 26px;
    --input-width: 26px;
    --input-border: 1px;

    position: relative;
    line-height: 1;

    height: calc(var(--input-height) - var(--input-border) * 2);

    width: 100%;
    min-width: var(--input-width);
    max-width: calc(var(--input-width) - var(--input-border) * 2);

    display: flex;
    justify-content: center;
    align-items: center;

    cursor: pointer;

    &:hover {
      &:before {
        --form-input-border-color: var(--c-blue-100);
        outline-color: var(--form-input-border-color);
        box-shadow: var(--form-input-box-shadow);
      }
    }

    &:before,
    &:after {
      position: absolute;
      top: 50%;
      left: 50%;

      transform: translate(-50%, -50%);
    }

    &:before {
      content: '';

      height: 100%;
      width: 100%;

      border: 1px solid var(--form-input-border-color);
      outline: 0.5px solid var(--outline-color);
      border-radius: var(--form-input-border-radius);
      box-shadow: none;

      background: var(--form-input-background);

      @include transitions(border, outline, background, box-shadow);
    }

    &:after {
      content: '';

      height: calc(var(--input-height) - 2px);
      width: calc(var(--input-width) - 2px);

      background: url('/img/check.svg') no-repeat center center;

      opacity: 0;
      visibility: hidden;

      @include transitions(opacity, visibility);
    }

    input {
      opacity: 0;

      height: 100%;
      width: 100%;

      cursor: pointer;
    }
  }

  .formkit-label {
    color: var(--c-gray-100);

    font-size: 18px;
    line-height: 26px;
    font-weight: 400;
    opacity: 1;
    letter-spacing: initial;
    text-transform: none;

    position: static;
    transform: translateY(0);

    &.html-label {
      p {
        font-size: inherit;
        line-height: inherit;
        font-weight: inherit;

        margin: 0;
      }
    }
  }
}

:deep(.checkbox-fieldset) {
  border: 0;
  padding: 0;
  .formkit-legend {
    margin-bottom: 20px;
  }
}

:deep(.formkit-options) {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
</style>
